export interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

type Listener = () => void;

let deferredPrompt: BeforeInstallPromptEvent | null = null;
let installConfirmed = false;
const listeners = new Set<Listener>();

const isStandalone = () => {
  if (typeof window === "undefined") return false;
  const navigatorWithStandalone = window.navigator as Navigator & { standalone?: boolean };
  return (
    window.matchMedia("(display-mode: standalone)").matches ||
    navigatorWithStandalone.standalone === true ||
    document.referrer.startsWith("android-app://")
  );
};

const notify = () => listeners.forEach((listener) => listener());

if (typeof window !== "undefined") {
  installConfirmed = isStandalone();

  window.addEventListener("beforeinstallprompt", (event) => {
    event.preventDefault();
    deferredPrompt = event as BeforeInstallPromptEvent;
    notify();
  });

  window.addEventListener("appinstalled", () => {
    deferredPrompt = null;
    installConfirmed = true;
    notify();
  });

  window.addEventListener("pageshow", () => {
    if (!isStandalone()) return;
    installConfirmed = true;
    deferredPrompt = null;
    notify();
  });
}

export const subscribeToPwaInstall = (listener: Listener) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

export const getPwaInstallSnapshot = () => ({
  canInstall: deferredPrompt !== null && !installConfirmed,
  isInstalled: installConfirmed || isStandalone(),
});

export const requestPwaInstall = async () => {
  const prompt = deferredPrompt;
  if (!prompt) return "unavailable" as const;

  await prompt.prompt();
  const { outcome } = await prompt.userChoice;
  deferredPrompt = null;
  notify();
  return outcome;
};